import { ArrayResolution, Filter } from '@fabric-ui/components';
import { getAllGroups } from '../../CustomDashboards/CustomDashboardsUtils';
import { DwQueryRequest, QueryObjectMetric, WidgetCategory } from '../../CustomDashboards/types';
import { VennSubType, VennTypeWidget } from '../../CustomDashboards/types/VennWidget.types';

export const getAssetsBySourcesMetric = (sourcesNames: string[]): QueryObjectMetric => ({
  name: `COUNT_DISTINCT_IF(asset._key, ${sourcesNames.map(sourceName => `ANY(asset.source_names) = '${sourceName}'`).join(' AND ')})`,
  alias: sourcesNames.join(', '),
});

export const getSourceFilter = (sourceName, cleanFilters, resolution = ArrayResolution.ANY): Filter => ({
  and: {
    operands: [
      {
        expression: {
          arrayCondition: {
            resolution,
            underlying: { fieldName: 'asset.source_names', stringCondition: { equals: sourceName } },
          },
        },
      },
      ...(cleanFilters ? [cleanFilters] : []),
    ],
  },
});

export const getSourcesFilter = (sourceNames, cleanFilters): Filter => ({
  and: {
    operands: [
      {
        expression: {
          arrayCondition: {
            resolution: ArrayResolution.COMPLETE,
            underlying: { fieldName: 'asset.source_names', stringCondition: { arrayEquals: { values: sourceNames } } },
          },
        },
      },
      ...(cleanFilters ? [cleanFilters] : []),
    ],
  },
});

export enum ScreenSize {
  xSmall,
  small,
  Medium,
}
export const sizeMap: Record<string, number> = {
  [ScreenSize.Medium]: 350,
  [ScreenSize.small]: 325,
};
export const maxSourcesNum = 3;

export const TOOL_COVERAGE_AND_GAP_GRID_LAYOUT = {
  [ScreenSize.xSmall]: [
    { i: 'a', x: 0, y: 0, w: 6, h: 10, static: true },
    { i: 'b', x: 6, y: 0, w: 6, h: 10, static: true },
    { i: 'c', x: 0, y: 10, w: 12, h: 13.32, static: true },
  ],
  [ScreenSize.small]: [
    { i: 'a', x: 0, y: 0, w: 6, h: 10, static: true },
    { i: 'b', x: 6, y: 0, w: 6, h: 10, static: true },
    { i: 'c', x: 0, y: 10, w: 12, h: 13.32, static: true },
  ],
  [ScreenSize.Medium]: [
    { i: 'a', x: 0, y: 0, w: 6, h: 10, static: true },
    { i: 'b', x: 6, y: 0, w: 6, h: 10, static: true },
    { i: 'c', x: 0, y: 10, w: 12, h: 13.32, static: true },
  ],
};

export const toolCoverageDefaultFilter = {
  vennSources: [],
};

export const getNormalizedBarWidth = ({ min, max, number }: { min: number; max: number; number: number }) =>
  ((number - min) / (max - min)) * (350 - 24) + 24 || 350;

export const getVennWidget = (interactiveVennSources: string[], filter?: Filter) => {
  const vennQuery: DwQueryRequest = {
    projectionId: {
      name: 'uber_assets',
      builtIn: true,
    },
    sorting: [],
    filter: {
      and: {
        operands: [
          {
            expression: {
              fieldName: 'asset.state',
              stringCondition: {
                in: {
                  values: ['ACTIVE'],
                },
              },
            },
          },
          ...(filter ? [filter] : []),
        ],
      },
    },
    select: {
      dims: [],
      metrics: getAllGroups(interactiveVennSources).map(sourceArray => getAssetsBySourcesMetric(sourceArray)),
    },
  };

  const vennWidget: VennTypeWidget = {
    id: 'vennWidget',
    requests: [vennQuery],
    definition: { title: { title: '' }, coords: { x: 0, y: 0, h: 0, w: 0 } },
    category: WidgetCategory.Venn,
    type: VennSubType.Venn,
  };
  return vennWidget;
};

/**
 * Generate the Venn Diagram arc ID for multiple keys.
 * - Transforms each key using key.replace(/[- ]+/g, '_').
 * - Sorts keys to ensure consistent order.
 * - Joins them with underscores.
 *
 * @param keys Array of keys involved in the arc.
 * @returns The transformed `id`.
 */
export const generateArcId = (keys: string[]): string =>
  keys
    .map(key => key.replace(/[- ]+/g, '_')) // Apply transformation to each key
    .sort() // Sort keys to ensure consistent order
    .join('_'); // Join transformed keys with underscores
